import { useEffect, useId, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { ApiError } from '../lib/api';
import { Popover, type PopoverProps } from './Popover';
import { ErrorNote } from './ui';

/**
 * A destructive button that asks first, in a small panel hung off the button.
 *
 * Suspending an organization, deactivating an account, retiring a period,
 * detaching a class, removing a holiday: each was one click with nothing in
 * the way, and the cost of a slip ranged from an awkward moment to a tenant
 * locked out. This puts the same short question in front of every one of
 * them, anchored where the finger already is, so the answer is one more tap
 * and never a hunt for a dialog.
 *
 * The panel is a form. Enter in a field confirms, which is what a field in a
 * confirmation ("the last day") wants, and its fields arrive in `onConfirm` as
 * FormData, uncontrolled, so a date input needs no state in the caller and
 * resets itself every time the panel opens, because the panel is unmounted
 * between openings.
 *
 * `onConfirm` returns a promise. Resolving closes the panel; rejecting shows
 * the reason inside it and re-enables the buttons, so a refusal from the
 * server is read where the question was asked rather than somewhere below.
 *
 * Focus lands on Cancel when there is nothing to fill in. The guard exists to
 * catch a slip, so the keystroke that follows must not be the thing it guards.
 */
export interface ConfirmProps {
  /** Contents of the button, e.g. "Suspend". */
  trigger: ReactNode;
  /** Classes for the button; a plain `btn` unless the caller has its own. */
  triggerClassName?: string;
  /** For a button whose contents do not say what it does. */
  triggerLabel?: string;
  /** Disables the button outright; nothing opens. */
  disabled?: boolean;
  /** Anything else the button needs: `aria-pressed`, an inline style. */
  triggerProps?: PopoverProps['triggerProps'];
  /** Which edge of the button the panel lines up with. */
  align?: 'start' | 'end';
  /** The question, naming the thing: "Suspend Grace Church?" */
  title: string;
  /** What happens, in a sentence or two. */
  description?: ReactNode;
  /** Fields between the text and the buttons, uncontrolled, with `name`s. */
  body?: ReactNode;
  confirmLabel: string;
  /** Shown on the confirming button while `onConfirm` is in flight. */
  pendingLabel: string;
  onConfirm: (form: FormData) => Promise<unknown>;
  /** Shown when the rejection carries no message of its own. */
  errorFallback?: string;
}

export function Confirm({
  trigger,
  triggerClassName = 'btn',
  triggerLabel,
  disabled = false,
  triggerProps,
  align = 'end',
  title,
  description,
  body,
  confirmLabel,
  pendingLabel,
  onConfirm,
  errorFallback = 'Could not do that.',
}: ConfirmProps) {
  const id = useId();
  const titleId = `${id}-title`;
  const descriptionId = description ? `${id}-description` : undefined;

  return (
    <Popover
      role="dialog"
      labelledBy={titleId}
      trigger={trigger}
      triggerClassName={triggerClassName}
      triggerLabel={triggerLabel}
      triggerProps={{ ...triggerProps, disabled }}
      align={align}
      panelClassName="confirm"
    >
      {({ close }) => (
        <Question
          titleId={titleId}
          descriptionId={descriptionId}
          title={title}
          description={description}
          body={body}
          confirmLabel={confirmLabel}
          pendingLabel={pendingLabel}
          onConfirm={onConfirm}
          errorFallback={errorFallback}
          close={close}
        />
      )}
    </Popover>
  );
}

/**
 * The panel's contents. A component of its own so that pending and error are
 * state that exists only while the panel is open: closing and reopening asks
 * afresh, with no stale refusal showing.
 */
function Question({
  titleId,
  descriptionId,
  title,
  description,
  body,
  confirmLabel,
  pendingLabel,
  onConfirm,
  errorFallback,
  close,
}: Pick<
  ConfirmProps,
  'title' | 'description' | 'body' | 'confirmLabel' | 'pendingLabel' | 'onConfirm'
> & {
  titleId: string;
  descriptionId: string | undefined;
  errorFallback: string;
  close: () => void;
}) {
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // A confirmed delete navigates away, and a press outside can close the panel
  // while the request is still in flight. Neither may write to state that is
  // no longer on screen.
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (pending) return;
    setPending(true);
    setError(null);
    try {
      await onConfirm(new FormData(event.currentTarget));
      if (mounted.current) close();
    } catch (cause) {
      if (!mounted.current) return;
      setError(cause instanceof ApiError ? cause.message : errorFallback);
      setPending(false);
    }
  };

  return (
    <form className="contents" onSubmit={(event) => void submit(event)}>
      <p id={titleId} className="text-sm font-semibold">
        {title}
      </p>
      {description ? (
        <p id={descriptionId} className="text-sm text-ink-soft">
          {description}
        </p>
      ) : null}
      {body}
      {error ? <ErrorNote message={error} /> : null}
      {/* Cancel first, so it is what the popover focuses on open. */}
      <div className="flex flex-wrap justify-end gap-2">
        <button type="button" className="btn" disabled={pending} onClick={close}>
          Cancel
        </button>
        <button type="submit" className="btn btn-danger" disabled={pending}>
          {pending ? pendingLabel : confirmLabel}
        </button>
      </div>
    </form>
  );
}
